/**
 * The outbox for problem reports to OverpoweredApps (2026-10-05).
 *
 * WHY A QUEUE. A report is most often made where the problem happened: at a
 * venue, often with no internet, sometimes before the person has signed in on
 * that device. A report that fails there must not be lost, so every report is
 * written here first and sent from here. The intake makes retries safe: the
 * same `id` twice is a no-op (OPApps/CONTRACTS/op-report.md section 2).
 *
 * WHAT EACH ANSWER DOES is the contract's table, kept literally:
 *   201 / 200  sent (200 = a retry of one already stored)  -> drop, show the server's words
 *   400 / 415  malformed - retrying cannot help            -> drop, tell the person
 *   409        the id is someone else's                    -> new id, send again
 *   413        too big                                     -> drop the attachments, send once more
 *   401        not signed in                               -> keep; send after sign-in
 *   429 / 5xx / no network                                 -> keep; retry later, backing off
 *
 * NEVER ON STAGE BY ITSELF. Background retries wait while the suite is in
 * performance mode (DEC-216: performance mode changes background work, and
 * this is background work). A report the person sends by hand goes at once -
 * they chose it.
 *
 * Bounded: a phone's storage is shared with the person's projects, so the
 * outbox keeps at most MAX_ITEMS / MAX_BYTES and lets the oldest automatic
 * report go first. Every storage touch is wrapped.
 */

import { createSignal, createEffect, createRoot, on, type Accessor } from 'solid-js';
import { opid, authStatus } from '../auth/opid.ts';
import { performanceMode } from '../opbridge/performanceMode.ts';
import { newReportId, type OutgoingReport } from './opReport.ts';
import { watchJob } from './watchdog.ts';

/** OPApps/CONTRACTS/op-report.md section 2 (moved off accounts. 2026-10-05). */
export const REPORT_INTAKE = 'https://overpoweredapps.net/api/reports';

const KEY = 'opbuttons.reports.outbox.v1';
const MAX_ITEMS = 8;
const MAX_BYTES = 1_500_000;
const TICK_MS = 30_000;
const BACKOFF_MIN_MS = 30_000;
const BACKOFF_MAX_MS = 30 * 60_000;

export type WaitReason = 'signin' | 'offline' | 'busy' | 'performing';

interface Item {
  out: OutgoingReport;
  queuedAt: number;
  tries: number;
  nextAt: number;
  waiting: WaitReason | null;
  trimmed: boolean;
}

export type SendOutcome =
  | { kind: 'sent'; id: string; message: string }
  | { kind: 'queued'; id: string; reason: WaitReason }
  | { kind: 'dropped'; id: string; reason: string };

function load(): Item[] {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as Item[]) : [];
  } catch {
    return [];
  }
}

const [items, setItems] = createSignal<Item[]>(load());

function persist(next: Item[]): void {
  let list = next.slice();
  const bytes = (l: Item[]) => JSON.stringify(l).length;
  while (list.length > MAX_ITEMS || (list.length > 1 && bytes(list) > MAX_BYTES)) {
    const auto = list.findIndex((i) => i.out.report.sent === 'auto');
    list.splice(auto >= 0 ? auto : 0, 1);
  }
  setItems(list);
  try { localStorage.setItem(KEY, JSON.stringify(list)); } catch { /* full: the in-memory copy still sends */ }
}

/** How many reports are waiting to send, and why the first one is waiting. */
export const outboxCount: Accessor<number> = () => items().length;
export const outboxReason: Accessor<WaitReason | null> = () => items()[0]?.waiting ?? null;

/* The OP ID token. Asked for only while signed in: otherwise the broker
   cannot have one, and asking from a non-suite origin (localhost) is a CORS
   error in the console for nothing (navigate smoke, 2026-10-05). Injectable
   ONLY so a sealed smoke can stand in for the suite broker, which a test
   browser cannot sign in to. */
let tokenSource: () => Promise<string | null> = () => (authStatus() === 'signed-in' ? opid.getToken() : Promise.resolve(null));
export function setReportTokenSourceForTests(fn: () => Promise<string | null>): void { tokenSource = fn; }

function formFor(out: OutgoingReport): FormData {
  const fd = new FormData();
  fd.append('report', JSON.stringify(out.report));
  for (const a of out.attachments) fd.append(a.name, new Blob([a.text], { type: a.type }), a.name);
  return fd;
}

/** What one POST did, as the contract defines it. */
type Answer =
  | { do: 'done'; message: string }
  | { do: 'drop'; reason: string }
  | { do: 'newid' }
  | { do: 'trim' }
  | { do: 'keep'; reason: WaitReason };

async function post(out: OutgoingReport): Promise<Answer> {
  const token = await tokenSource().catch(() => null);
  if (!token) return { do: 'keep', reason: 'signin' };
  let res: Response;
  try {
    // credentials: 'omit' - the OP ID token travels in the header; a cookie
    // on this same-site origin must never ride along (audits/CHECKS.md).
    res = await fetch(REPORT_INTAKE, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formFor(out),
      credentials: 'omit',
    });
  } catch {
    return { do: 'keep', reason: 'offline' };
  }
  const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string; field?: string; detail?: string };
  if (res.status === 201 || res.status === 200) {
    return { do: 'done', message: body.message || 'Sent. Thanks - you can follow it in My reports.' };
  }
  if (res.status === 409) return { do: 'newid' };
  if (res.status === 413) return { do: 'trim' };
  if (res.status === 401) return { do: 'keep', reason: 'signin' };
  if (res.status === 400 || res.status === 415) {
    return { do: 'drop', reason: `The report could not be read (${body.error ?? res.status}${body.field ? `, ${body.field}` : ''}).` };
  }
  return { do: 'keep', reason: 'busy' };
}

let draining = false;
const results = new Map<string, (o: SendOutcome) => void>();

function settle(id: string, o: SendOutcome): void {
  const done = results.get(id);
  if (done) { results.delete(id); done(o); }
}

/** Try every due report once. `force` sends even while performing (the
 *  person pressed Send) and ignores the backoff. */
async function drain(force = false): Promise<void> {
  watchJob('report-outbox');
  if (draining) return;
  draining = true;
  try {
    for (const item of items().slice()) {
      const id = item.out.report.id;
      if (!force && performanceMode()) { mark(id, { waiting: 'performing' }); settle(id, { kind: 'queued', id, reason: 'performing' }); continue; }
      if (!force && item.nextAt > Date.now()) continue;
      let current = item;
      for (let step = 0; step < 4; step++) {
        const a = await post(current.out);
        if (a.do === 'newid') {
          const before = current.out.report.id;
          const fresh = newReportId();
          current = { ...current, out: { ...current.out, report: { ...current.out.report, id: fresh } } };
          replace(before, current);
          const waiter = results.get(before);
          if (waiter) { results.delete(before); results.set(fresh, waiter); }
          continue;
        }
        if (a.do === 'trim' && !current.trimmed) {
          // Our only attachment is the black box text, so "drop the ones over
          // the limit" is dropping it; the summary under `data` still goes.
          current = { ...current, trimmed: true, out: { report: { ...current.out.report, attachments: [] }, attachments: [] } };
          replace(current.out.report.id, current);
          continue;
        }
        const rid = current.out.report.id;
        if (a.do === 'done') { remove(rid); settle(rid, { kind: 'sent', id: rid, message: a.message }); }
        else if (a.do === 'drop' || a.do === 'trim') {
          remove(rid);
          settle(rid, { kind: 'dropped', id: rid, reason: a.do === 'trim' ? 'The report was too big to send.' : a.reason });
        } else {
          const tries = current.tries + 1;
          const wait = a.reason === 'signin' ? BACKOFF_MIN_MS : Math.min(BACKOFF_MAX_MS, BACKOFF_MIN_MS * 2 ** (tries - 1));
          mark(rid, { tries, nextAt: Date.now() + wait, waiting: a.reason });
          settle(rid, { kind: 'queued', id: rid, reason: a.reason });
        }
        break;
      }
    }
  } finally {
    draining = false;
  }
}

function replace(id: string, next: Item): void {
  persist(items().map((i) => (i.out.report.id === id ? next : i)));
}
function mark(id: string, patch: Partial<Item>): void {
  persist(items().map((i) => (i.out.report.id === id ? { ...i, ...patch } : i)));
}
function remove(id: string): void {
  persist(items().filter((i) => i.out.report.id !== id));
}

/** Put a report in the outbox and try to send it now. Resolves to what
 *  happened to THIS report: sent, waiting (and why), or dropped. */
export function submitReport(out: OutgoingReport): Promise<SendOutcome> {
  const id = out.report.id;
  persist([...items(), { out, queuedAt: Date.now(), tries: 0, nextAt: 0, waiting: null, trimmed: false }]);
  const outcome = new Promise<SendOutcome>((resolve) => results.set(id, resolve));
  void drain(out.report.sent === 'manual');
  return outcome;
}

/** Retry whatever is waiting now (Settings' "Send now"). */
export function retryOutbox(): Promise<void> { return drain(true); }

let installed = false;
/** Start the background retries. Idempotent; called once from App. */
export function installReportOutbox(): void {
  if (installed) return;
  installed = true;
  setInterval(() => { if (items().length) void drain(); }, TICK_MS);
  window.addEventListener('online', () => { if (items().length) void drain(true); });
  createRoot(() => {
    // Signing in, and leaving performance mode, are the two moments a
    // waiting report most likely becomes sendable.
    createEffect(on(authStatus, (s) => { if (s === 'signed-in' && items().length) void drain(true); }, { defer: true }));
    createEffect(on(performanceMode, (p) => { if (!p && items().length) void drain(true); }, { defer: true }));
  });
}
